import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { CheckCheck, ArrowRight } from 'lucide-react'
import { Button } from '@workspace/ui/components/Button'
import { Spinner } from '@workspace/ui/components/Spinner'
import { NotificationItem } from './NotificationItem'
import type { Notification, NotificationCategory } from '@workspace/lib/api/notification'

interface NotificationListProps {
    category: NotificationCategory
    notifications: Notification[]
    hasNextPage?: boolean
    fetchNextPage?: () => void
    isFetchingNextPage?: boolean
    onClose?: () => void
    onItemClick?: (notification: Notification) => void
    onMarkAllAsRead?: () => void
    onViewAll?: () => void
    onViewAllFooter?: () => void
}

const TITLE_MAP: Record<NotificationCategory, string> = {
    general: 'Thông báo',
    approval: 'Phê duyệt',
    warning: 'Cảnh báo',
}

export function NotificationList({
    category,
    notifications,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    onItemClick,
    onMarkAllAsRead,
    onViewAll,
    onViewAllFooter,
}: NotificationListProps) {
    const { t } = useTranslation()
    const { ref, inView } = useInView()

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage?.()
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

    return (
        <div className="w-90 bg-background rounded-[8px] border border-border box-content shadow-[0_2px_4px_-1px_rgba(0,0,0,0.06),0_4px_6px_-1px_rgba(0,0,0,0.1)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-11 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">{TITLE_MAP[category]}</h3>

                {/* Thông báo: "Đánh dấu đã đọc", Phê duyệt + Cảnh báo: "Xem tất cả →" */}
                {category === 'general' ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="px-0 text-sm font-semibold text-primary hover:text-primary/90 hover:bg-transparent gap-1"
                        onClick={onMarkAllAsRead}
                    >
                        <CheckCheck className="size-4!" />
                        Đánh dấu đã đọc
                    </Button>
                ) : (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="px-0 h-5 text-sm font-semibold text-primary hover:text-primary/90 hover:bg-transparent gap-1"
                        onClick={onViewAll}
                    >
                        Xem tất cả
                        <ArrowRight className="size-4!" />
                    </Button>
                )}
            </div>

            {/* Notification list */}
            <div className="max-h-[400px] overflow-y-auto">
                {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <p className="text-sm">Không có thông báo nào</p>
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {notifications.map(notification => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onClick={() => onItemClick?.(notification)}
                            />
                        ))}
                        {hasNextPage && (
                            <div
                                ref={ref}
                                className="p-3 flex justify-center items-center gap-2 border-t border-border min-h-[48px]"
                            >
                                {isFetchingNextPage && (
                                    <>
                                        <Spinner className="size-5 text-primary" />
                                        <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            {category === 'general' && (
                <div className="border-t border-border p-2 flex justify-center">
                    <Button
                        variant="ghost"
                        className="w-full text-sm font-semibold text-primary hover:text-primary/90"
                        onClick={onViewAllFooter}
                    >
                        Xem tất cả
                    </Button>
                </div>
            )}
        </div>
    )
}
